import { Component, ViewChild, OnInit } from '@angular/core';
import { I18nService } from 'i18n/i18n.service';
import { AIForProcessesService } from './services/ai-for-processes.service';
import { IItemTreeview } from './typings/IItemTreeview';
import { IKBCatalogCategory, IKBCatalogSelection, IWorkerConfiguration } from './interfaces/base-knowledge-catalog';
import { IKnowledgeData } from './interfaces/knowledge-data';
import { BzgModalService } from 'bz-genome-angular';
import { DeleteConfirmationComponent } from 'app/shared/components/delete-confirmation/delete-confirmation.component';
import { ProcessesTreeViewComponent } from './processes-tree-view/processes-tree-view.component';
import { IWorkerConfigurationData } from './interfaces/worker-configuration-data';
import { ObjectType } from './typings/ObjectType';
import { ShowToastService } from 'app/shared/services/showToast.service';

@Component({
  selector: 'biz-ai-for-processes',
  templateUrl: './ai-for-processes.component.html',
  styleUrls: ['./ai-for-processes.component.scss']
})
export class AIForProcessesComponent implements OnInit {
  catalogsLoaded = false;
  categoriesLoaded = false;
  isCollapsed = false;
  hasOverride = false;
  processesAny: boolean | null = null;
  isLoading = false;
  selected: IItemTreeview = {} as IItemTreeview;
  knowledgeCategories: IKBCatalogCategory[];
  knowledgeData: IKnowledgeData;
  workerConfigurationData: IWorkerConfigurationData;
  listNodeForPath: IItemTreeview[] = [];
  applications: any[] = [];
  maxVisibleNodes: number = 3; // cantidad de nodos visibles (breadcrumb)

  @ViewChild(ProcessesTreeViewComponent) treeViewComponent!: ProcessesTreeViewComponent;

  constructor(
    private _i18nService: I18nService,
    private _aiForProcessesService: AIForProcessesService,
    private _modalService: BzgModalService,
    private _showToastService: ShowToastService) { }

  ngOnInit(): void {
    this.loadApplications();
  }

  private loadApplications(): void {
    this._aiForProcessesService.getApplications().subscribe({
      next: (res) => {
        this.applications = res;
        this.processesAny = res.length > 0; // true si hay datos, false si no hay

        if (this.processesAny) {
          this.loadKnowledgebases();
        }
      },
      error: (error) => {
        this.processesAny = false; // false en caso de error
      }
    });
  }

  loadKnowledgebases(): void {
    this._aiForProcessesService.getKnowledgebases().subscribe({
      next: (response) => {
        this.knowledgeCategories = response;
        this.categoriesLoaded = true;
      },
      error: (error) => {
        this.categoriesLoaded = false;
      },
      complete: () => {
        // La carga ya está completa cuando processesAny se establece
      }
    });
  }

  onSelect(node: IItemTreeview) {
    this.renderNodePath(node);
    this.selected = node;

    // Crear el objeto combinado
    this.knowledgeData = {
      aIBpmKnowledgeBase: node.aIBpmKnowledgeBase,
      inheritanceInfo: node.inheritanceInfo
    };

    this.workerConfigurationData = {
      aIWorkerConfiguration: node.aIWorkerConfiguration,
      inheritanceInfo: node.inheritanceInfo?.inheritedWorkerConfiguration
    };

    this.hasOverride = node.hasOverride;
    this.categoriesLoaded = true;
  }

  onAIWorkerConfigurationChange(config: IWorkerConfiguration) {
    this.workerConfigurationData.aIWorkerConfiguration = config;

    this._aiForProcessesService.saveAIWorkerConfiguration(this.selected.id, this.workerConfigurationData.aIWorkerConfiguration).subscribe({
      next: (response) => {
        if (this.treeViewComponent) {
          this.treeViewComponent.onSelect(this.selected);
        }
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
      },
      error: (error) => {
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      },
      complete: () => {
        // this.isLoading = false;
      }
    });
  }

  resetAiWorkerConfiguration(id: string) {
    const title = `${this._i18nService.instant('GLOBAL.RESET')} - ${this.selected.displayName}`
    const modal = this._modalService.open(DeleteConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: { title, message: 'GLOBAL.CONFIRM_RESET_AIWORKER', button: 'GLOBAL.RESET' }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this._aiForProcessesService.resetAIWorkerConfiguration(id).subscribe({
          next: (response) => {
            if (this.treeViewComponent) {
              this.treeViewComponent.onSelect(this.selected);
            }
            this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
          },
          error: (error) => {
            this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
          },
          complete: () => {
            // this.isLoading = false;
          }
        });
      }
    });
  }

  handleCollapseChange(isCollapsed: boolean) {
    this.isCollapsed = isCollapsed;
  }

  getNodeTypeIcon(node: IItemTreeview): string {
    switch (node.type) {
      case ObjectType.ROOT: return 'biz-icon-path';
      case ObjectType.CATEGORY: return 'folder-path';
      case ObjectType.PROCESS: return 'diagram-path';
      case ObjectType.VERSION: return 'timer-path';
      case ObjectType.ACTIVITY: return 'activity-path';
      case ObjectType.SUBPROCESS: return 'process-path';
      case ObjectType.REUSABLE_GROUP: return 'reusable-path';
      case ObjectType.EMBEDDED_GROUP: return 'embedded-path';
      default: return 'diagram';
    }
  }

  getNodeDisplayName(node: IItemTreeview): string {
    if (node.type === ObjectType.VERSION) {
      return `${this._i18nService.instant('AIFORPROCESSES.OBJECTTYPE.VERSION')} ${node.displayName}`;
    }
    return node.displayName;
  }

  getVisiblePath(): IItemTreeview[] {
    if (this.listNodeForPath.length <= this.maxVisibleNodes) {
      return this.listNodeForPath;
    }
    return this.listNodeForPath.slice(-this.maxVisibleNodes);
  }

  shouldShowEllipsis(): boolean {
    return this.listNodeForPath.length > this.maxVisibleNodes;
  }

  getLastPath(){    
    if(this.listNodeForPath && this.listNodeForPath.length > 0)
      return this.getNodeDisplayName(this.listNodeForPath[this.listNodeForPath.length - 1])      
    return null;
  }

  onCatalogSelectionChanged(kBCatalogSelection: IKBCatalogSelection): void {
    this.isLoading = true;
    kBCatalogSelection.idItem = this.selected.id;

    this._aiForProcessesService.saveAIBpmKnowledgeBaseEnvironment(kBCatalogSelection).subscribe({
      next: () => {
        if (this.treeViewComponent) {
          this.treeViewComponent.onSelect(this.selected);
        }
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
      },
      error: () => {
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onRemoveAIBpmKnowledgeBaseEnvironmentItem(kBCatalogSelection: IKBCatalogSelection): void {
    this.isLoading = true;
    kBCatalogSelection.idItem = this.selected.id;

    this._aiForProcessesService.removeAIBpmKnowledgeBaseEnvironmentItem(kBCatalogSelection).subscribe({
      next: () => {
        if (this.treeViewComponent) {
          this.treeViewComponent.onSelect(this.selected);
        }
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
      },
      error: () => {
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private renderNodePath(node: IItemTreeview) {
    this.listNodeForPath = [];
    this.listNodeForPath = this.findRootNode(node, this.listNodeForPath);
    this.listNodeForPath.reverse();
  }

  private findRootNode(node: IItemTreeview, listNode: IItemTreeview[]): IItemTreeview[] {
    listNode.push(node);
    if (node.level == 0 || node.parent == undefined)
      return listNode;
    return this.findRootNode(node.parent, listNode);
  }
}
